import {
  formatMoney,
  recurrenceLabelKey,
  type ExternalCost,
  type Metafield,
} from '@forecastify/shared';
import { PersonFace } from '../person-face';
import { Badge } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { Attachments } from '../attachments';
import {
  DetailFrame,
  InlineTitle,
  MoreMenu,
  RailCard,
  RailDate,
  RailLink,
  RailMoney,
  RailPicker,
  RailRow,
  RailValue,
  SectionHeader,
} from './detail-chrome';
import { MetafieldsCard } from './metafields-card';
import { CollapsibleHistory } from '../history/collapsible-history';
import type { CostPatch } from './external-cost-dialog';
import { RailEditLink } from './rail-edit-link';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C62 · A külső költség — `extcost-detail.png`, transcribed.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SCREEN C56 OWED AND DID NOT BUILD.** The mockup ships `ExternalCostDetail` as one of
 * its nine detail components, with the same standing an invoice has; C56 built the list, the
 * dialog and an attachments strip and stopped. Two things had no surface at all as a result: the
 * periods a recurring cost lands on, and its metafields — `0132` put `external_cost` in
 * `metafield_entity` and the settings screen offers the entity, so the product was letting somebody
 * define fields that nothing rendered.
 *
 * ⚠️⚠️ **ÉRINTETT IDŐSZAKOK IS THE POINT OF THE SCREEN, AND IT IS A LIST RATHER THAN A FIGURE.**
 * The tab already says *12 időszak · 2 160 000 Ft*; what nobody could see is WHICH twelve, which is
 * the question asked the moment a total looks wrong. The rows come from the same series the count
 * came from (`occurrencesFor`), so the list and the multiplier cannot disagree.
 *
 * ⚠️ **Every row carries the FULL amount, and the note above says so.** A period's share of a
 * recurring licence is the whole licence — `PHASE-4.md` records the reading, and a column of equal
 * figures is what a reader needs to see to believe the total.
 */

export interface ExternalCostDetailData {
  readonly cost: ExternalCost;
  /** C30 · the workspace's `external_cost` metafields. Empty when none are defined. */
  readonly metafields: readonly Metafield[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  readonly currencies: readonly { readonly code: string; readonly symbol: string }[];
}

export interface ExternalCostDetailHandlers {
  readonly canManage: boolean;
  /**
   * ⚠️ ux-pass-2 · one field changed in place — the name, the date, the amount, the currency, the
   * owner. The caller merges it onto the record and sends the whole body with its `version`.
   */
  readonly onPatch: (change: CostPatch) => void;
  /** What it is attached to, in `ExternalCostDialog`'s `focus="link"`. */
  readonly onEditLink: () => void;
  /** How often it recurs, in `ExternalCostDialog`'s `focus="recurrence"`. */
  readonly onEditRecurrence: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
  readonly onMetafields: (values: Record<string, unknown>) => void;
  readonly onOpenProject: () => void;
  readonly onOpenLink: () => void;
  readonly onOpenOwner: () => void;
  readonly onOpenPeriod: (startsOn: string) => void;
  readonly onAttach: (file: File) => Promise<string | null>;
  /** Only on the drawer and the modal — the page is already full. */
  readonly onFull?: () => void;
}

/** ⚠️ The mockup's own period colours — the same three `occurrenceState` answers. */
const STATE_DOT: Record<'planned' | 'active' | 'closed', string> = {
  planned: 'var(--status-idle)',
  active: 'var(--status-ok)',
  closed: 'var(--status-info)',
};

/** The link in words. ⚠️ Exported because the list column and this screen must read alike. */
export function costLinkLabel(t: Translate, cost: ExternalCost): string {
  if (cost.linkKind === 'line_item') return cost.lineItemName ?? t('extcost.link.lineItem');
  if (cost.linkKind === 'occurrence') {
    return `${cost.contractIdentifier ?? ''} · ${cost.occurrenceStartsOn ?? ''}`.trim();
  }
  return t('extcost.link.project');
}

/** Whether the cost's link names something with a page of its own. */
export function costLinkReachable(cost: ExternalCost): boolean {
  if (cost.linkKind === 'line_item') {
    return cost.contractIdentifier !== null && cost.lineItemIdentifier !== null;
  }
  if (cost.linkKind === 'occurrence') return cost.contractIdentifier !== null;
  return false;
}

export function costLinkKindLabel(t: Translate, cost: ExternalCost): string {
  if (cost.linkKind === 'line_item') return t('extcost.link.lineItem');
  if (cost.linkKind === 'occurrence') return t('extcost.link.occurrence');
  return t('extcost.link.project');
}

/** ⚠️ One `t()` per branch with a LITERAL key — a computed one is invisible to `i18n:check`. */
export function costRecurrenceLabel(t: Translate, cost: ExternalCost): string {
  const key = recurrenceLabelKey(cost.recurrenceUnit, cost.recurrenceStep);
  if (key === 'once') return t('extcost.recur.once');
  if (key === 'weekly') return t('extcost.recur.weekly');
  if (key === 'monthly') return t('extcost.recur.monthly');
  const step = cost.recurrenceStep ?? 1;
  return key === 'everyNWeeks'
    ? t('extcost.recur.everyNWeeks', { step })
    : t('extcost.recur.everyNMonths', { step });
}

export function ExternalCostDetail({
  data,
  handlers,
  wide,
}: {
  readonly data: ExternalCostDetailData;
  readonly handlers: ExternalCostDetailHandlers;
  readonly wide: boolean;
}) {
  const t = useT();
  const cost = data.cost;
  const recurring = cost.recurrenceUnit !== null;

  /* 22 Oct 2026 · a project reader without its money sees the cost, not its figures — and does
     not edit it, because a cost is saved whole (`project-reach.ts`). */
  const showMoney = cost.moneyVisible;
  const canManage = handlers.canManage && showMoney;
  const symbol =
    data.currencies.find((one) => one.code === cost.currencyCode)?.symbol ??
    cost.currencyCode ??
    '';
  const money = (amount: string | null): string =>
    amount === null || cost.currencyCode === null
      ? ''
      : formatMoney(Number(amount), { code: cost.currencyCode, symbol });

  /* 28 Oct 2026 · the owner's photo where they have one — `PersonFace`. */
  const ownerFace = (
    <PersonFace
      name={cost.ownerName}
      seed={cost.ownerAvatarSeed}
      url={cost.ownerAvatarUrl}
      colorKey={cost.ownerMemberId}
    />
  );

  return (
    <DetailFrame
      wide={wide}
      header={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-[12px] tabular-nums">
              {cost.identifier}
            </span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-[12px] transition-colors"
              onClick={handlers.onOpenProject}
            >
              <span
                className="size-2 rounded-[3px]"
                style={{ background: cost.projectColor ?? 'var(--status-idle)' }}
              />
              {cost.projectName}
            </button>
            {recurring && <Badge variant="secondary">{costRecurrenceLabel(t, cost)}</Badge>}

            <div className="flex-1" />

            {/* ⚠️ No *Szerkesztés* — ux-pass-2, 14 Oct: *"Why is there an edit button here to edit
                the details? This should be inline like all other parts."* The title and every rail
                row are the controls now; what is left in the kebab is what is not a field. */}
            {canManage ? (
              <>
                <MoreMenu
                  label={t('extcost.action.more')}
                  items={[
                    ...(handlers.onFull === undefined
                      ? []
                      : [
                          {
                            label: t('extcost.action.full'),
                            icon: 'maximize' as const,
                            onSelect: handlers.onFull,
                          },
                        ]),
                    {
                      label: t('extcost.action.duplicate'),
                      icon: 'copy',
                      onSelect: handlers.onDuplicate,
                    },
                    {
                      label: t('extcost.action.openLink'),
                      icon: 'arrow-right',
                      disabled: !costLinkReachable(cost),
                      onSelect: handlers.onOpenLink,
                    },
                    { type: 'separator' },
                    {
                      label: t('extcost.action.delete'),
                      icon: 'trash',
                      destructive: true,
                      onSelect: handlers.onDelete,
                    },
                  ]}
                />
              </>
            ) : null}
          </div>

          <InlineTitle
            value={cost.name}
            canEdit={canManage}
            onSave={(name) => {
              handlers.onPatch({ name });
            }}
          />
        </>
      }
      rail={
        <>
          {showMoney && (
            <RailCard title={t('extcost.amount')} padded>
              <div className="mt-1 text-[22px] leading-none tabular-nums">{money(cost.amount)}</div>
              {/* ⚠️⚠️ **THE SENTENCE BEFORE THE FIGURE CAN BE MISREAD.** A line item's budget two
                tables away DIVIDES over its periods; this multiplies. The mockup writes it out on
                this card for that reason, and the dialog says it again before the amount is typed. */}
              <div className="text-muted-foreground mt-1.5 text-[11.5px] leading-relaxed">
                {recurring ? t('extcost.amount.recurNote') : t('extcost.amount.onceNote')}
              </div>
              {recurring && (
                <div className="mt-2 flex items-center justify-between border-t pt-2 text-[11.5px]">
                  <span className="text-muted-foreground">{t('extcost.total')}</span>
                  <span className="tabular-nums">
                    {`${money(cost.total)} · ${t('extcost.periodCount', { count: cost.periods })}`}
                  </span>
                </div>
              )}
            </RailCard>
          )}

          <RailCard title={t('extcost.details')}>
            {/**
             * ⚠️⚠️ **EVERY ROW IS ITS OWN CONTROL — ux-pass-2, 14 Oct.** The *Szerkesztés* dialog
             * is gone; a single value is edited where it is read, as on the line item. The link and
             * the recurrence are several fields that only make sense together, so their rows open
             * a dialog asking just that — `RailEditLink`.
             */}
            {showMoney && (
              <>
                <RailRow label={t('extcost.field.amount')}>
                  {canManage ? (
                    <RailMoney
                      label={t('extcost.field.amount')}
                      value={cost.amount ?? ''}
                      suffix={symbol}
                      onCommit={(amount) => {
                        /* ⚠️ Empty is not a figure a cost can have — it reverts rather than writing
                       zero, which the dialog would have refused too. */
                        if (amount !== null) handlers.onPatch({ amount });
                      }}
                    />
                  ) : (
                    <RailValue>{money(cost.amount)}</RailValue>
                  )}
                </RailRow>
                <RailRow label={t('extcost.field.currency')}>
                  <RailPicker
                    label={t('extcost.field.currency')}
                    value={`${cost.currencyCode ?? ''} · ${symbol}`}
                    disabled={!canManage}
                    items={data.currencies.map((one) => ({
                      label: `${one.code} · ${one.symbol}`,
                      checked: one.code === cost.currencyCode,
                      onSelect: () => {
                        if (one.code !== cost.currencyCode) {
                          handlers.onPatch({ currencyCode: one.code });
                        }
                      },
                    }))}
                  />
                </RailRow>
              </>
            )}
            <RailRow label={t('extcost.field.date')}>
              {canManage ? (
                <RailDate
                  label={t('extcost.field.date')}
                  value={cost.incurredOn}
                  clearable={false}
                  onChange={(incurredOn) => {
                    if (incurredOn !== '' && incurredOn !== cost.incurredOn) {
                      handlers.onPatch({ incurredOn });
                    }
                  }}
                />
              ) : (
                <RailValue>{cost.incurredOn}</RailValue>
              )}
            </RailRow>
            <RailRow label={t('extcost.field.link')}>
              <RailEditLink
                value={costLinkLabel(t, cost)}
                before={
                  <Badge variant={cost.linkKind === 'project' ? 'outline' : 'secondary'}>
                    {costLinkKindLabel(t, cost)}
                  </Badge>
                }
                editLabel={t('extcost.field.link')}
                openLabel={t('extcost.action.openLink')}
                {...(canManage ? { onEdit: handlers.onEditLink } : {})}
                /* ⚠️ Only when the target has an address — an arrow that cannot go anywhere is a
                   button that silently does nothing. */
                {...(costLinkReachable(cost) ? { onOpen: handlers.onOpenLink } : {})}
              />
            </RailRow>
            <RailRow label={t('extcost.field.recurrence')}>
              {canManage ? (
                <RailEditLink
                  value={recurrenceNote(t, cost)}
                  editLabel={t('extcost.field.recurrence')}
                  openLabel={t('extcost.field.recurrence')}
                  onEdit={handlers.onEditRecurrence}
                />
              ) : (
                <RailValue mono={false}>{recurrenceNote(t, cost)}</RailValue>
              )}
            </RailRow>
            <RailRow label={t('extcost.field.owner')} divided>
              {canManage ? (
                <RailPicker
                  label={t('extcost.field.owner')}
                  value={cost.ownerName}
                  searchPlaceholder={t('list.searchOptions')}
                  before={ownerFace}
                  items={data.members.map((one) => ({
                    label: one.name,
                    checked: one.id === cost.ownerMemberId,
                    onSelect: () => {
                      if (one.id !== cost.ownerMemberId) {
                        handlers.onPatch({ ownerMemberId: one.id });
                      }
                    },
                  }))}
                />
              ) : (
                <RailLink
                  value={cost.ownerName}
                  title={cost.ownerName}
                  before={ownerFace}
                  onClick={handlers.onOpenOwner}
                />
              )}
            </RailRow>
            <RailRow label={t('extcost.field.created')}>
              <RailValue>{cost.createdAt.slice(0, 10)}</RailValue>
            </RailRow>
          </RailCard>

          <MetafieldsCard
            fields={data.metafields}
            values={cost.metafields}
            members={data.members}
            canEdit={canManage}
            onSave={handlers.onMetafields}
          />
        </>
      }
      main={
        <>
          {recurring && (
            <section>
              <SectionHeader
                title={t('extcost.occurrences')}
                note={
                  showMoney
                    ? t('extcost.occurrences.note', {
                        count: cost.occurrences.length,
                        amount: money(cost.amount),
                      })
                    : undefined
                }
              />
              {cost.occurrences.length === 0 ? (
                /**
                 * ⚠️ **A recurring cost with no periods is an ordinary state, not an error.** Its
                 * project may have no recurring contract behind it at all — it is still charged
                 * once, which is what the rail's total says. Drawing an empty table instead of
                 * this sentence would read as a failed load.
                 */
                <p className="text-muted-foreground mt-1.5 rounded-lg border px-3 py-6 text-center text-[11.5px]">
                  {t('extcost.occurrences.empty')}
                </p>
              ) : (
                <div className="mt-1.5 overflow-hidden rounded-lg border">
                  <div className="text-muted-foreground bg-muted/40 flex items-center gap-3 border-b px-3 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
                    <span className="w-10 shrink-0">#</span>
                    <span className="min-w-[132px] flex-1">{t('extcost.occ.period')}</span>
                    <span className="w-[86px] shrink-0">{t('extcost.occ.state')}</span>
                    <span className="w-[110px] shrink-0 text-right">
                      {t('extcost.column.amount')}
                    </span>
                  </div>
                  {cost.occurrences.map((one) => (
                    <button
                      key={one.from}
                      type="button"
                      onClick={() => {
                        handlers.onOpenPeriod(one.from);
                      }}
                      className={[
                        'hover:bg-muted/50 flex w-full items-center gap-3 border-b px-3 py-2 text-left transition-colors last:border-0',
                        /* ⚠️ The period we are IN is tinted — the mockup's own `rowCls`. It is the
                           row somebody is looking for when they open this list today. */
                        one.state === 'active' ? 'bg-primary/5' : '',
                      ].join(' ')}
                    >
                      <span className="text-muted-foreground w-10 shrink-0 text-[11px] tabular-nums">
                        {`#${String(one.index)}`}
                      </span>
                      <span className="min-w-[132px] flex-1 truncate text-[11.5px] tabular-nums">
                        {`${one.from} – ${one.to}`}
                      </span>
                      <span className="flex w-[86px] shrink-0 items-center gap-1.5 text-[11.5px]">
                        <span
                          className="size-1.5 rounded-full"
                          style={{ background: STATE_DOT[one.state] }}
                        />
                        {stateLabel(t, one.state)}
                      </span>
                      {showMoney && (
                        <span className="w-[110px] shrink-0 text-right text-[11.5px] tabular-nums">
                          {money(cost.amount)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          <section>
            <SectionHeader title={t('extcost.files')} />
            <div className="mt-1.5">
              <Attachments
                target="external_cost"
                targetId={cost.id}
                canEdit={canManage}
                emptyNote={t('extcost.files.empty')}
                onUpload={async (files) => {
                  for (const file of files) await handlers.onAttach(file);
                }}
              />
            </div>
          </section>

          {/* ux-pass-2 · the real audit trail, `/history/external_cost/:id`, closed until opened. */}
          <CollapsibleHistory type="external_cost" id={cost.id} />
        </>
      }
    />
  );
}

function stateLabel(t: Translate, state: 'planned' | 'active' | 'closed'): string {
  if (state === 'planned') return t('extcost.occ.planned');
  if (state === 'active') return t('extcost.occ.active');
  return t('extcost.occ.closed');
}

/**
 * The recurrence in a sentence — *Havonta · 2026-01-01-tól (nyitott végű)*.
 *
 * ⚠️ **The open-ended case is said out loud.** A recurrence with no end runs to the contract's own
 * last period, and a blank where the end date goes reads as *"somebody forgot to fill this in"*.
 */
function recurrenceNote(t: Translate, cost: ExternalCost): string {
  if (cost.recurrenceUnit === null) return t('extcost.recur.onceNote');
  const label = costRecurrenceLabel(t, cost);
  return cost.recurrenceUntil === null
    ? `${label} · ${t('extcost.recur.fromOpen', { from: cost.incurredOn })}`
    : `${label} · ${t('extcost.recur.fromTo', { from: cost.incurredOn, to: cost.recurrenceUntil })}`;
}
